<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
    @click.self="onCancel"
  >
    <div class="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col max-h-[90vh]">
      <!-- Header -->
      <header class="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200">
        <div class="min-w-0">
          <h3 class="font-semibold text-slate-900 truncate">{{ $t('wishlist.title') }}</h3>
          <p class="text-xs text-slate-500 truncate">{{ product?.nom || product?.title || $t('reviewPage.productDefault') }}</p>
        </div>
        <button class="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 shrink-0" @click="onCancel" :aria-label="$t('common.close')">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </header>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-5 py-4">
        <div v-if="fetching" class="space-y-2">
          <div v-for="i in 3" :key="i" class="h-12 rounded-lg bg-slate-100 animate-pulse" />
        </div>

        <div v-else-if="!lists.length && !creating" class="text-center py-6">
          <p class="text-sm text-slate-600 mb-3">{{ $t('wishlist.empty') }}</p>
          <button class="h-10 px-4 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600" @click="creating = true">
            {{ $t('wishlist.createFirst') }}
          </button>
        </div>

        <ul v-else class="space-y-2">
          <li v-for="list in lists" :key="list.id">
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-lg border transition-colors text-left"
              :class="selectedListId === list.id
                ? 'border-brand-500 bg-brand-50'
                : 'border-slate-200 bg-white hover:bg-slate-50'"
              @click="selectedListId = list.id"
            >
              <span
                class="h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0"
                :class="selectedListId === list.id ? 'border-brand-500 bg-brand-500' : 'border-slate-300 bg-white'"
              >
                <svg v-if="selectedListId === list.id" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium text-slate-900 truncate">{{ list.name }}</span>
                <span class="block text-xs text-slate-500">{{ $t('product.available', { n: (list.produits || []).length }) }}</span>
              </span>
              <span v-if="initialListId === list.id" class="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium shrink-0">{{ $t('wishlist.current') }}</span>
            </button>
          </li>
        </ul>

        <!-- Create new list -->
        <div v-if="lists.length || creating" class="mt-3">
          <button v-if="!creating" type="button" class="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-dashed border-slate-300 text-sm font-medium text-brand-600 hover:bg-brand-50 hover:border-brand-300" @click="creating = true">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14m-7-7h14"/></svg>
            {{ $t('wishlist.createNew') }}
          </button>
          <div v-else class="space-y-2">
            <input
              v-model="newListName"
              ref="newListInput"
              type="text"
              :placeholder="$t('wishlist.namePlaceholder')"
              class="w-full h-10 px-3 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              @keydown.enter.prevent="submitCreateList"
            />
            <p v-if="createError" class="text-xs text-red-600">{{ createError }}</p>
            <div class="flex items-center gap-2">
              <button class="h-9 px-3 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100" @click="cancelCreate" :disabled="creatingSubmit">{{ $t('common.cancel') }}</button>
              <button class="h-9 px-3 rounded-md bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-50" :disabled="creatingSubmit" @click="submitCreateList">
                {{ creatingSubmit ? '…' : $t('wishlist.create') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-2">
        <button
          v-if="initialListId"
          class="h-10 px-3 rounded-md text-sm font-medium text-red-600 hover:bg-red-50"
          :disabled="submitting"
          @click="onRemove"
        >
          {{ $t('wishlist.remove') }}
        </button>
        <span v-else />
        <div class="flex items-center gap-2">
          <button class="h-10 px-3 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100" :disabled="submitting" @click="onCancel">{{ $t('common.cancel') }}</button>
          <button
            class="h-10 px-4 rounded-md bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 disabled:opacity-50"
            :disabled="!canConfirm || submitting"
            @click="onConfirm"
          >
            {{ submitting ? '…' : confirmLabel }}
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { toasts } from '@/services/toast';
import i18n from '@/i18n';

const t = (key, params) => i18n.global.t(key, params);

export default {
  name: 'WishlistPickerDialog',
  emits: ['close', 'changed'],
  props: {
    open: { type: Boolean, default: false },
    product: { type: Object, default: null },
    clientId: { type: [Number, String], default: null },
  },
  data() {
    return {
      lists: [],
      fetching: false,
      submitting: false,
      selectedListId: null,
      initialListId: null, // list the product is currently in (if any)
      creating: false,
      newListName: '',
      createError: '',
      creatingSubmit: false,
    };
  },
  computed: {
    canConfirm() {
      // Confirm enabled if a list is picked AND it differs from where the product currently sits.
      return this.selectedListId != null && this.selectedListId !== this.initialListId;
    },
    confirmLabel() {
      return this.initialListId ? t('wishlist.move') : t('wishlist.add');
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(v) {
        if (v) this.boot();
      },
    },
  },
  methods: {
    async boot() {
      this.creating = false;
      this.newListName = '';
      this.createError = '';
      this.selectedListId = null;
      this.initialListId = null;
      if (!this.clientId || !this.product?.id) return;
      this.fetching = true;
      try {
        const r = await axios.get(`https://scap.ma/api/getLists/${this.clientId}`);
        const list = Array.isArray(r.data) ? r.data : [];
        this.lists = list.map((l) => ({
          ...l,
          produits: Array.isArray(l.produits) ? l.produits : [],
        }));
        // Find which list (if any) currently contains the product.
        const inList = this.lists.find((l) => (l.produits || []).some((p) => Number(p.id) === Number(this.product.id)));
        if (inList) {
          this.initialListId = inList.id;
          this.selectedListId = inList.id;
        } else if (this.lists.length === 1) {
          // Single list: pre-select so the user can confirm in one tap.
          this.selectedListId = this.lists[0].id;
        }
      } catch (e) {
        toasts.error(t('wishlist.loadFail'), e.response?.data?.message || e.message);
      } finally {
        this.fetching = false;
      }
    },
    cancelCreate() {
      this.creating = false;
      this.newListName = '';
      this.createError = '';
    },
    async submitCreateList() {
      const name = (this.newListName || '').trim();
      if (!name) { this.createError = t('wishlist.nameRequired'); return; }
      this.creatingSubmit = true;
      this.createError = '';
      try {
        const r = await axios.post('https://scap.ma/api/liste', {
          name,
          client_id: this.clientId,
        });
        const created = r.data?.id ? r.data : { id: r.data?.liste?.id, name };
        if (!created?.id) { await this.boot(); return; }
        this.lists.push({ ...created, name, produits: [] });
        this.selectedListId = created.id;
        this.creating = false;
        this.newListName = '';
      } catch (e) {
        this.createError = e.response?.data?.message
          || (e.response?.data?.errors && Object.values(e.response.data.errors)[0]?.[0])
          || t('wishlist.createFail');
      } finally {
        this.creatingSubmit = false;
      }
    },
    async onConfirm() {
      if (!this.canConfirm || this.submitting) return;
      this.submitting = true;
      try {
        // POST is idempotent on the backend; if the product is already in
        // another list of the same client, store() deletes it from there
        // and inserts into the chosen list — exactly what we want.
        await axios.post('https://scap.ma/api/clientliste', {
          produit_id: this.product.id,
          client_id: this.clientId,
          liste_id: this.selectedListId,
        });
        const list = this.lists.find((l) => l.id === this.selectedListId);
        toasts.success(t('wishlist.addedToList'), list?.name || '');
        this.$emit('changed', { wishlisted: true, listId: this.selectedListId, listName: list?.name });
        this.$emit('close');
      } catch (e) {
        toasts.error(t('wishlist.actionFail'), e.response?.data?.message || e.message);
      } finally {
        this.submitting = false;
      }
    },
    async onRemove() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await axios.delete(`https://scap.ma/api/clientliste/${this.clientId}/${this.product.id}`);
        toasts.success(t('wishlist.removedFromList'));
        this.$emit('changed', { wishlisted: false, listId: null });
        this.$emit('close');
      } catch (e) {
        toasts.error(t('wishlist.actionFail'), e.response?.data?.message || e.message);
      } finally {
        this.submitting = false;
      }
    },
    onCancel() {
      if (this.submitting) return;
      this.$emit('close');
    },
  },
};
</script>
